import * as d3 from 'd3';
import { Theme } from '../../../theme';

interface ContestLineProps {
  chartArea: d3.Selection<SVGGElement, unknown, null, undefined>;
  xScale: d3.ScaleTime<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  theme: Theme;
  chartHeight: number;
}

/**
 * Renders the Bitcoin Conference 2026 target date line (April 28, 2026)
 * with pulsing animation and label
 */
export const renderContestTargetLine = ({
  chartArea,
  xScale,
  yScale,
  theme,
  chartHeight
}: ContestLineProps): void => {
  const contestDate = new Date('2026-04-28T23:00:00Z'); // 4PM PT = 23:00 UTC
  const now = new Date();
  
  // Only show if contest is active (before evaluation)
  if (now > contestDate) return;
  
  // Check if date is in visible range
  const xDomain = xScale.domain();
  if (contestDate < xDomain[0] || contestDate > xDomain[1]) return;
  
  const xPos = xScale(contestDate);
  const yDomain = yScale.domain();
  
  // Remove existing contest elements
  chartArea.selectAll('.contest-target-group').remove();
  
  // Create group for contest elements
  const contestGroup = chartArea.append('g')
    .attr('class', 'contest-target-group');
  
  // Add SVG defs for gradient and glow
  let defs = chartArea.select<SVGDefsElement>('defs.contest-defs');
  if (defs.empty()) {
    defs = chartArea.append('defs').attr('class', 'contest-defs');
    
    // Vertical gradient for the line
    const gradient = defs.append('linearGradient')
      .attr('id', 'contest-line-gradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%');
    
    gradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', theme.primary)
      .attr('stop-opacity', 0.1);
    
    gradient.append('stop')
      .attr('offset', '50%')
      .attr('stop-color', theme.primary)
      .attr('stop-opacity', 0.8);
    
    gradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', theme.primary)
      .attr('stop-opacity', 0.1);
    
    // Glow filter
    const filter = defs.append('filter')
      .attr('id', 'contest-glow')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');
    
    filter.append('feGaussianBlur')
      .attr('stdDeviation', '3')
      .attr('result', 'coloredBlur');
    
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');
  }
  
  // Main vertical line with gradient
  contestGroup.append('line')
    .attr('class', 'contest-line')
    .attr('x1', xPos)
    .attr('y1', 0)
    .attr('x2', xPos)
    .attr('y2', chartHeight)
    .attr('stroke', 'url(#contest-line-gradient)')
    .attr('stroke-width', 3)
    .attr('stroke-dasharray', '8,4')
    .style('filter', 'url(#contest-glow)');
  
  // Pulsing highlight line - FULL HEIGHT
  contestGroup.append('line')
    .attr('class', 'contest-pulse-line')
    .attr('x1', xPos)
    .attr('y1', 0)
    .attr('x2', xPos)
    .attr('y2', chartHeight)
    .attr('stroke', theme.primary)
    .attr('stroke-width', 2)
    .attr('opacity', 0.5)
    .style('animation', 'contestPulse 2s ease-in-out infinite');
  
  // Days until contest calculation
  const daysUntil = Math.ceil((contestDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  // Top label background
  const labelY = 30;
  const labelWidth = 160;
  const labelHeight = 50;
  
  contestGroup.append('rect')
    .attr('x', xPos - labelWidth / 2)
    .attr('y', labelY - 10)
    .attr('width', labelWidth)
    .attr('height', labelHeight)
    .attr('rx', 8)
    .attr('fill', theme.surface)
    .attr('stroke', theme.primary)
    .attr('stroke-width', 2)
    .style('filter', 'url(#contest-glow)');
  
  // Label text - "CONTEST"
  contestGroup.append('text')
    .attr('x', xPos)
    .attr('y', labelY + 8)
    .attr('text-anchor', 'middle')
    .attr('fill', theme.primary)
    .attr('font-size', '12px')
    .attr('font-weight', '700')
    .text('🎯 CONTEST');
  
  // Date text
  contestGroup.append('text')
    .attr('x', xPos)
    .attr('y', labelY + 24)
    .attr('text-anchor', 'middle')
    .attr('fill', theme.text)
    .attr('font-size', '11px')
    .text('April 28, 4PM PT');

  // Countdown badge at bottom
  const badgeY = chartHeight - 40;
  const badgeWidth = 90;
  const badgeHeight = 26;
  
  contestGroup.append('rect')
    .attr('x', xPos - badgeWidth / 2)
    .attr('y', badgeY)
    .attr('width', badgeWidth)
    .attr('height', badgeHeight)
    .attr('rx', 13)
    .attr('fill', theme.primary)
    .style('filter', 'url(#contest-glow)');
  
  contestGroup.append('text')
    .attr('x', xPos)
    .attr('y', badgeY + 17)
    .attr('text-anchor', 'middle')
    .attr('fill', '#000')
    .attr('font-size', '11px')
    .attr('font-weight', '700')
    .text(`${daysUntil}d left`);
};

// Add CSS animation for pulse effect
const addContestAnimationStyles = () => {
  const styleId = 'contest-animation-styles';
  if (document.getElementById(styleId)) return;
  
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes contestPulse {
      0%, 100% { opacity: 0.3; stroke-width: 2px; }
      50% { opacity: 0.8; stroke-width: 4px; }
    }
  `;
  document.head.appendChild(style);
};

// Call this when module loads
addContestAnimationStyles();

export default renderContestTargetLine;
